/**
 * FieldGroup - Groups fields by user interaction type (Verification vs New Information)
 * Replaces confidence-based grouping with user-centric organization
 */

import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import VerifiedIcon from '@mui/icons-material/Verified';
import EditIcon from '@mui/icons-material/Edit';
import { styled } from '@mui/material/styles';
import { ExtractedField } from '../../types';
import { CONFIDENCE_HIGH_THRESHOLD } from '../../config/validation';
import HighlightableField from './HighlightableField';
import ReadOnlyField from './ReadOnlyField';
import StatusBadge from '../shared/StatusBadge';

const GroupCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'groupType',
})<{ groupType: 'verification' | 'newInformation' }>(({ theme, groupType }) => ({
  marginBottom: theme.spacing(3),
  border: `2px solid ${
    groupType === 'verification' 
      ? theme.palette.info.light 
      : theme.palette.primary.light
  }`,
  '& .MuiCardContent-root': {
    paddingBottom: theme.spacing(2),
  },
}));

const GroupHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const GroupTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

interface FieldGroupProps {
  title: string;
  description: string;
  fields: ExtractedField[];
  groupType: 'verification' | 'newInformation';
  readOnlyFields?: string[]; // Field names that should remain read-only
  validatedFields: Record<string, string>;
  onFieldChange: (fieldId: string, value: string) => void;
  disabled?: boolean;
}

const FieldGroup: React.FC<FieldGroupProps> = ({
  title,
  description,
  fields,
  groupType,
  readOnlyFields = [],
  validatedFields,
  onFieldChange,
  disabled = false,
}) => {
  // Calculate group statistics
  const totalFields = fields.length;
  const lowConfidenceFields = fields.filter(f => f.confidence < CONFIDENCE_HIGH_THRESHOLD).length;
  const requiredFields = fields.filter(f => f.required).length;

  const getGroupIcon = () => {
    return groupType === 'verification' ? (
      <VerifiedIcon color="info" />
    ) : (
      <EditIcon color="primary" />
    );
  };

  const getStatusChip = () => {
    if (groupType === 'verification') {
      return (
        <Chip
          label="For Reference"
          size="small"
          color="info"
          variant="outlined"
        />
      );
    }
    
    return (
      <Chip
        label="Submit to System"
        size="small"
        color="primary"
        variant="outlined"
      />
    );
  };

  return (
    <GroupCard groupType={groupType}>
      <CardContent>
        <GroupHeader>
          <GroupTitle>
            {getGroupIcon()}
            <Box>
              <Typography variant="h6" component="h3">
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            </Box>
          </GroupTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {getStatusChip()}
            <Chip
              label={`${totalFields} fields`}
              size="small"
              variant="outlined"
            />
            {lowConfidenceFields > 0 && (
              <StatusBadge type="confidence" confidence={CONFIDENCE_HIGH_THRESHOLD - 1} />
            )}
          </Box>
        </GroupHeader>

        <Box>
          {fields.map((field) => {
            const fieldValue = validatedFields[field.id] || field.value || '';
            const isFieldReadOnly = readOnlyFields.includes(field.name);
            
            return isFieldReadOnly ? (
              <ReadOnlyField
                key={field.id}
                field={field}
                value={fieldValue}
              />
            ) : (
              <HighlightableField
                key={field.id}
                field={field}
                value={fieldValue}
                onChange={(value) => onFieldChange(field.id, value)}
                disabled={disabled}
              />
            );
          })}
        </Box>

        {requiredFields > 0 && groupType === 'newInformation' && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            * {requiredFields} required field{requiredFields !== 1 ? 's' : ''}
          </Typography>
        )}
      </CardContent>
    </GroupCard>
  );
};

export default FieldGroup;